import { downloadCsv, toCsvRow } from '../../utils/downloadCsv';
import { Z1_BANK_ACCOUNTS } from './zReport1Data';
import { getGymExportBrand, getGymExportSlug } from '../../utils/gymExportBrand';
import {
  getExportFileDate,
  getExportTimestamp,
} from '../../utils/professionalExport';
import {
  buildThemedPrintCss,
  buildZReportPrintExtras,
  buildThemedExcelCss,
  wrapExcelHtmlDocument,
  excelEscape,
  excelNumber,
  excelTd,
  excelTh,
  excelBlankRow,
} from '../../utils/themeExport';
import { downloadPairedZReportPdf } from './zReportPdfExport';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function money(value) {
  const n = Number(value) || 0;
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function formatReportDate(isoDay) {
  const day = String(isoDay || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return day || '—';
  const d = new Date(`${day}T12:00:00`);
  if (Number.isNaN(d.getTime())) return day;
  return `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}`;
}

function displayManual(value) {
  if (value === '' || value == null) return '0';
  return money(value);
}

/** PDF/print cell — blank/zero → dash like the Excel template. */
function pdfCell(value) {
  if (value === '' || value == null || value === '—' || value === '-') return '-';
  const raw = String(value).replace(/,/g, '').trim();
  if (!raw) return '-';
  const n = Number(raw);
  if (Number.isFinite(n) && n === 0) return '-';
  if (Number.isFinite(n)) return money(n);
  return String(value);
}

function pdfTd(value, { strong = false, num = true } = {}) {
  const shown = pdfCell(value);
  const isDash = shown === '-';
  const cls = isDash ? 'dash' : (num ? 'num' : '');
  const inner = strong ? `<strong>${escapeHtml(shown)}</strong>` : escapeHtml(shown);
  return `<td class="${cls}">${inner}</td>`;
}

function pdfTwoColBody(rows) {
  return (rows || []).map(([label, value, strong]) => (
    strong
      ? `<tr class="total"><td><strong>${escapeHtml(label)}</strong></td>${pdfTd(value, { strong: true })}</tr>`
      : `<tr><td>${escapeHtml(label)}</td>${pdfTd(value)}</tr>`
  )).join('');
}

function pdfThreeColBody(rows) {
  return (rows || []).map(([a, b, c, strong]) => (
    strong
      ? `<tr class="total"><td><strong>${escapeHtml(a)}</strong></td>${pdfTd(b, { strong: true })}${pdfTd(c, { strong: true })}</tr>`
      : `<tr><td>${escapeHtml(a)}</td>${pdfTd(b)}${pdfTd(c)}</tr>`
  )).join('');
}

function pdfBankBody(rows) {
  return (rows || []).map((row) => {
    const strong = row[row.length - 1] === true;
    const cells = strong ? row.slice(0, -1) : row;
    const [label, ...values] = cells;
    const valueTds = values.map((v) => pdfTd(v, { strong })).join('');
    return strong
      ? `<tr class="total"><td><strong>${escapeHtml(label)}</strong></td>${valueTds}</tr>`
      : `<tr><td>${escapeHtml(label)}</td>${valueTds}</tr>`;
  }).join('');
}

function buildBankGridRows(manual, closingBanks) {
  const fieldRow = (label, field) => [
    label,
    ...Z1_BANK_ACCOUNTS.map((acc) => displayManual(manual.bankAccounts?.[acc]?.[field])),
  ];
  return [
    fieldRow('Opening Balance', 'opening'),
    fieldRow('Bank Received', 'received'),
    fieldRow('Bank Sale', 'sale'),
    fieldRow('Pearl Group Expenses', 'pearlGroupExpenses'),
    fieldRow('Gym Expenses', 'gymExpenses'),
    [
      'Closing Balance',
      ...Z1_BANK_ACCOUNTS.map((acc) => money(closingBanks?.[acc])),
      true,
    ],
  ];
}

function bankGridRowsHtml(rows, { excel = false } = {}) {
  const td = excel ? 'cell' : '';
  return (rows || []).map((row) => {
    const strong = row[row.length - 1] === true;
    const cells = strong ? row.slice(0, -1) : row;
    const [label, ...values] = cells;
    const valueTds = values.map((v) => (
      strong
        ? `<td class="num ${td}"><strong>${escapeHtml(v)}</strong></td>`
        : `<td class="num ${td}">${escapeHtml(v)}</td>`
    )).join('');
    return strong
      ? `<tr class="total"><td class="${td}"><strong>${escapeHtml(label)}</strong></td>${valueTds}</tr>`
      : `<tr><td class="${td}">${escapeHtml(label)}</td>${valueTds}</tr>`;
  }).join('');
}

function buildPayloadTables(payload) {
  const {
    gymTitle = {},
    reportDate,
    auto = {},
    manual = {},
    closingCash = 0,
    closingBanks = {},
    gymExpenseTotal = 0,
    pearlExpenseTotal = 0,
    saleSummaryGrandTotal = 0,
    saleMethodsGrandTotal = 0,
    pearlGroupCash = 0,
  } = payload;

  const saleSummary = (auto.saleSummary || []).map((row) => ([
    row.label,
    Number(row.count) || 0,
    row.value ? money(row.value) : '0',
  ]));
  saleSummary.push([
    'Total Sale',
    Number(auto.totalSaleCount) || 0,
    money(auto.totalSaleValue),
    true,
  ]);

  const paymentMethods = (auto.paymentMethods || []).map((row) => ([
    row.label,
    Number(row.count) || 0,
    row.amount ? money(row.amount) : '0',
  ]));
  paymentMethods.push([
    'Total Received',
    Number(auto.totalReceivedCount) || 0,
    money(auto.totalReceivedValue),
    true,
  ]);

  const computedSaleSummaryGrand = saleSummaryGrandTotal
    || Object.values(manual.totalSaleSummary || {}).reduce((sum, v) => sum + (Number(v) || 0), 0);

  const saleSummaryManual = [
    ['New Membership', displayManual(manual.totalSaleSummary?.new_membership)],
    ['Renewal Sale', displayManual(manual.totalSaleSummary?.renewal)],
    ['Pt Sale', displayManual(manual.totalSaleSummary?.pt_sale)],
    ['Guest Pass', displayManual(manual.totalSaleSummary?.guest_pass)],
    ['Locker Sale', displayManual(manual.totalSaleSummary?.locker_sale)],
    ['Spa Sale', displayManual(manual.totalSaleSummary?.spa_sale)],
    ['Oxybar Sale', displayManual(manual.totalSaleSummary?.oxybar)],
    ['Zumba Sale', displayManual(manual.totalSaleSummary?.zumba_sale)],
    ['Yoga Sale', displayManual(manual.totalSaleSummary?.yoga_sale)],
    ['Gym Apparel', displayManual(manual.totalSaleSummary?.gym_apparel)],
    ['Misc Sale', displayManual(manual.totalSaleSummary?.miscellaneous_sale)],
    ['Remaining Clear', displayManual(manual.totalSaleSummary?.remaining_clear)],
    ['Total Sale', money(computedSaleSummaryGrand), true],
  ];

  const expenseSummary = [
    ['Gym Cash Expense', displayManual(manual.totalExpenseSummary?.gym_cash)],
    ['Gym Bank Expense', displayManual(manual.totalExpenseSummary?.gym_bank)],
    ['Total', money(gymExpenseTotal), true],
    ['Pearl Cash Expense', displayManual(manual.totalExpenseSummary?.pearl_cash)],
    ['Pearl Bank Expense', displayManual(manual.totalExpenseSummary?.pearl_bank)],
    ['Total', money(pearlExpenseTotal), true],
  ];

  const saleMethods = [
    ['Cash', displayManual(manual.totalSaleMethods?.cash)],
    ['Credit Card', displayManual(manual.totalSaleMethods?.card)],
    ['Bank Transfer', displayManual(manual.totalSaleMethods?.bank)],
    ['Qr', displayManual(manual.totalSaleMethods?.qr)],
    ['Total Received', money(saleMethodsGrandTotal), true],
  ];

  const activeMembers = [
    ['Membership During Month', displayManual(manual.totalActiveMembers?.membership_sale_month)],
    ['Pt During Month', displayManual(manual.totalActiveMembers?.pt_sale_month)],
    ['Active Member Women', displayManual(manual.totalActiveMembers?.active_women)],
    ['Active Member Men', displayManual(manual.totalActiveMembers?.active_men)],
  ];

  return {
    brandName: gymTitle.name || getGymExportBrand().name || 'Z Report',
    branch: gymTitle.branch || '',
    dayLabel: formatReportDate(reportDate),
    day: String(reportDate || '').slice(0, 10),
    cashRows: [
      ['Opening Balance', displayManual(manual.openingCash)],
      ['Cash Received', displayManual(manual.cashReceived)],
      ['Today Cash Sale', money(auto.cashSale)],
      ['Pearl Group Expenses', displayManual(manual.pearlGroupCash ?? pearlGroupCash)],
      ['Gym Expenses', money(auto.cashExpenses)],
      ['Closing Balance', money(closingCash), true],
    ],
    bankRows: buildBankGridRows(manual, closingBanks),
    bankAccounts: Z1_BANK_ACCOUNTS,
    saleSummary,
    paymentMethods,
    saleSummaryManual,
    expenseSummary,
    saleMethods,
    activeMembers,
  };
}

/** Print / PDF HTML — Excel-template layout, app theme colors. */
function buildPrintHtmlContent(payload) {
  const data = buildPayloadTables(payload);
  const bankCols = data.bankAccounts.map((acc) => `<th class="zr-col">${escapeHtml(acc)}</th>`).join('');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Z-Report — ${escapeHtml(data.day)}</title>
  <style>
    ${buildThemedPrintCss(buildZReportPrintExtras(), { compact: true, fitOnePage: true })}
  </style>
</head>
<body class="fit-one-page z-report-print">
  <table class="zr-banner">
    <tr><td class="zr-brand">${escapeHtml(data.brandName)}</td></tr>
    ${data.branch ? `<tr><td class="zr-branch">${escapeHtml(data.branch)}</td></tr>` : ''}
    <tr><td>
      <div class="zr-meta">
        <span>Z-REPORT</span>
        <span>DATE : ${escapeHtml(data.dayLabel)}</span>
      </div>
    </td></tr>
  </table>

  <div class="zr-pair">
    <table>
      <thead>
        <tr><th class="zr-sec" colspan="4">Bank Summery</th></tr>
        <tr><th class="zr-col">Item</th>${bankCols}</tr>
      </thead>
      <tbody>${pdfBankBody(data.bankRows)}</tbody>
    </table>
    <div class="zr-pair-gap" aria-hidden="true"></div>
    <table>
      <thead>
        <tr><th class="zr-sec" colspan="2">Cash Summery</th></tr>
        <tr><th class="zr-col">Item</th><th class="zr-col">Amount</th></tr>
      </thead>
      <tbody>${pdfTwoColBody(data.cashRows)}</tbody>
    </table>
  </div>

  <div class="zr-pair">
    <table>
      <thead>
        <tr><th class="zr-sec" colspan="3">Today Sale Summery</th></tr>
        <tr><th class="zr-col">Item</th><th class="zr-col">No</th><th class="zr-col">Amount</th></tr>
      </thead>
      <tbody>${pdfThreeColBody(data.saleSummary)}</tbody>
    </table>
    <div class="zr-pair-gap" aria-hidden="true"></div>
    <table>
      <thead>
        <tr><th class="zr-sec" colspan="2">During Month Sale Summery</th></tr>
        <tr><th class="zr-col">Item</th><th class="zr-col">Amount</th></tr>
      </thead>
      <tbody>${pdfTwoColBody(data.saleSummaryManual)}</tbody>
    </table>
  </div>

  <div class="zr-pair">
    <table>
      <thead>
        <tr><th class="zr-sec" colspan="3">Today Payment Method</th></tr>
        <tr><th class="zr-col">Item</th><th class="zr-col">No</th><th class="zr-col">Amount</th></tr>
      </thead>
      <tbody>${pdfThreeColBody(data.paymentMethods)}</tbody>
    </table>
    <div class="zr-pair-gap" aria-hidden="true"></div>
    <table>
      <thead>
        <tr><th class="zr-sec" colspan="2">During Month Payment Method</th></tr>
        <tr><th class="zr-col">Item</th><th class="zr-col">Amount</th></tr>
      </thead>
      <tbody>${pdfTwoColBody(data.saleMethods)}</tbody>
    </table>
  </div>

  <div class="zr-pair">
    <table>
      <thead>
        <tr><th class="zr-sec" colspan="2">Total Active Members</th></tr>
        <tr><th class="zr-col">Item</th><th class="zr-col">Amount</th></tr>
      </thead>
      <tbody>${pdfTwoColBody(data.activeMembers)}</tbody>
    </table>
    <div class="zr-pair-gap" aria-hidden="true"></div>
    <table>
      <thead>
        <tr><th class="zr-sec" colspan="2">Total Expense Summery</th></tr>
        <tr><th class="zr-col">Item</th><th class="zr-col">Amount</th></tr>
      </thead>
      <tbody>${pdfTwoColBody(data.expenseSummary)}</tbody>
    </table>
  </div>
</body>
</html>`;
}

/**
 * Excel — same style as Detailed Report: stacked sections, fixed 3 columns.
 * No side-by-side / gap columns (Excel misplaces those).
 * Cols: A = label | B = count (or blank) | C = amount
 */
function buildExcelHtmlContent(payload) {
  const data = buildPayloadTables(payload);
  const e = excelEscape;

  const sectionTitle = (title) => (
    `<tr>${excelTh(e(title), { colspan: 3 })}</tr>`
  );

  const twoColRows = (rows) => (rows || []).map(([label, value, strong]) => {
    const amount = excelNumber(value);
    return `<tr>
      ${excelTd(e(label), { tot: strong, bold: strong })}
      ${excelTd('', { tot: strong })}
      ${excelTd(amount === '' ? 0 : amount, { num: true, tot: strong, bold: strong })}
    </tr>`;
  }).join('');

  const threeColRows = (rows) => (rows || []).map(([label, count, value, strong]) => {
    const countNum = (count === '—' || count === '' || count == null)
      ? ''
      : excelNumber(count);
    const amount = excelNumber(value);
    return `<tr>
      ${excelTd(e(label), { tot: strong, bold: strong })}
      ${excelTd(countNum === '' ? (count === '—' ? '0' : '') : countNum, {
        num: true,
        tot: strong,
        bold: strong,
        center: true,
      })}
      ${excelTd(amount === '' ? 0 : amount, { num: true, tot: strong, bold: strong })}
    </tr>`;
  }).join('');

  // Prefer numeric counts (0 instead of "—") for Excel
  const saleSummary = (data.saleSummary || []).map((row) => {
    const [label, count, value, strong] = row;
    const n = Number(String(count).replace(/,/g, ''));
    const countOut = Number.isFinite(n) ? n : 0;
    return [label, countOut, value, strong];
  });
  const paymentMethods = (data.paymentMethods || []).map((row) => {
    const [label, count, value, strong] = row;
    const n = Number(String(count).replace(/,/g, ''));
    const countOut = Number.isFinite(n) ? n : 0;
    return [label, countOut, value, strong];
  });

  const body = `
<table class="sheet">
  <colgroup>
    <col style="width:55%" />
    <col style="width:15%" />
    <col style="width:30%" />
  </colgroup>
  <tr>${excelTh(e(data.brandName), { colspan: 3, cls: 'hdr' })}</tr>
  ${data.branch ? `<tr>${excelTh(e(data.branch), { colspan: 3, cls: 'subhdr' })}</tr>` : ''}
  <tr>
    ${excelTh('Z Report', { colspan: 2, cls: 'subhdr' })}
    ${excelTh(e(data.dayLabel), { cls: 'subhdr' })}
  </tr>
  ${excelBlankRow(3)}
  ${sectionTitle('Cash Summary')}
  <tr>
    ${excelTh('Item')}
    ${excelTh('')}
    ${excelTh('Amount')}
  </tr>
  ${twoColRows(data.cashRows)}
  ${excelBlankRow(3)}
  ${sectionTitle('Bank Summery')}
  <tr>
    ${excelTh('Item')}
    ${data.bankAccounts.map((acc) => excelTh(acc)).join('')}
  </tr>
  ${bankGridRowsHtml(data.bankRows, { excel: true })}
  ${excelBlankRow(3)}
  ${sectionTitle('Sale Summary')}
  <tr>
    ${excelTh('Item')}
    ${excelTh('NO')}
    ${excelTh('VALUE')}
  </tr>
  ${threeColRows(saleSummary)}
  ${excelBlankRow(3)}
  ${sectionTitle('Total Sale Summary')}
  <tr>
    ${excelTh('Item')}
    ${excelTh('')}
    ${excelTh('Amount')}
  </tr>
  ${twoColRows(data.saleSummaryManual)}
  ${excelBlankRow(3)}
  ${sectionTitle('Payment Method')}
  <tr>
    ${excelTh('Item')}
    ${excelTh('NO')}
    ${excelTh('SUM')}
  </tr>
  ${threeColRows(paymentMethods)}
  ${excelBlankRow(3)}
  ${sectionTitle('Total Payment Method')}
  <tr>
    ${excelTh('Item')}
    ${excelTh('')}
    ${excelTh('Amount')}
  </tr>
  ${twoColRows(data.saleMethods)}
  ${excelBlankRow(3)}
  ${sectionTitle('Total Active Member')}
  <tr>
    ${excelTh('Item')}
    ${excelTh('')}
    ${excelTh('Amount')}
  </tr>
  ${twoColRows(data.activeMembers)}
  ${excelBlankRow(3)}
  ${sectionTitle('Total Expense Summary')}
  <tr>
    ${excelTh('Item')}
    ${excelTh('')}
    ${excelTh('Amount')}
  </tr>
  ${twoColRows(data.expenseSummary)}
</table>`;

  return wrapExcelHtmlDocument({
    title: `Z-Report — ${data.day}`,
    sheetName: 'Z Report',
    bodyHtml: body,
    css: buildThemedExcelCss(),
    fitWidth: 1,
    fitHeight: 0,
  });
}

function buildCsvContent(payload) {
  const data = buildPayloadTables(payload);
  const brand = getGymExportBrand();
  const lines = [
    data.brandName || brand.name,
    data.branch,
    `Z-Report — ${data.dayLabel}`,
    `Generated: ${getExportTimestamp()}`,
    '',
    'Cash Summary',
    toCsvRow(['Item', 'Amount']),
    ...data.cashRows.map(([label, value]) => toCsvRow([label, value])),
    '',
    'Bank Summery',
    toCsvRow(['Item', ...data.bankAccounts]),
    ...data.bankRows.map((row) => {
      const strong = row[row.length - 1] === true;
      const cells = strong ? row.slice(0, -1) : row;
      return toCsvRow(cells);
    }),
    '',
    'Sale Summary',
    toCsvRow(['Item', 'NO', 'VALUE']),
    ...data.saleSummary.map(([a, b, c]) => toCsvRow([a, b, c])),
    '',
    'Total Sale Summary',
    toCsvRow(['Item', 'Amount']),
    ...data.saleSummaryManual.map(([label, value]) => toCsvRow([label, value])),
    '',
    'Payment Method',
    toCsvRow(['Item', 'NO', 'SUM']),
    ...data.paymentMethods.map(([a, b, c]) => toCsvRow([a, b, c])),
    '',
    'Total Payment Method',
    toCsvRow(['Item', 'Amount']),
    ...data.saleMethods.map(([label, value]) => toCsvRow([label, value])),
    '',
    'Total Active Member',
    toCsvRow(['Item', 'Amount']),
    ...data.activeMembers.map(([label, value]) => toCsvRow([label, value])),
    '',
    'Total Expense Summary',
    toCsvRow(['Item', 'Amount']),
    ...data.expenseSummary.map(([label, value]) => toCsvRow([label, value])),
  ].filter((line) => line !== undefined && line !== null);
  return lines.join('\n');
}

function downloadBlob(content, filename, type) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 200);
}

function fileBase(payload) {
  const slug = getGymExportSlug() || 'gym';
  const day = String(payload.reportDate || getExportFileDate()).slice(0, 10);
  return `z-report-${slug}-${day}`;
}

function openHtmlDocument(html, { autoPrint = false } = {}) {
  // Blob URL — never document.write into a window (can wipe the React app
  // when the browser reuses the current tab / blocks real pop-ups).
  let url = '';
  try {
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    url = URL.createObjectURL(blob);
  } catch (err) {
    console.error('Z Report HTML blob failed', err);
    return false;
  }

  const revokeLater = () => {
    window.setTimeout(() => {
      try { URL.revokeObjectURL(url); } catch { /* ignore */ }
    }, 60_000);
  };

  if (autoPrint) {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('title', 'Z Report Print');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;';
    document.body.appendChild(iframe);
    const cleanup = () => {
      window.setTimeout(() => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        try { URL.revokeObjectURL(url); } catch { /* ignore */ }
      }, 60_000);
    };
    iframe.onload = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch {
        // ignore
      }
      cleanup();
    };
    iframe.src = url;
    return true;
  }

  const win = window.open(url, '_blank');
  // Some browsers return null with noopener, or reuse the current window.
  if (!win || win === window) {
    // Safe fallback: download HTML instead of navigating this tab away
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = 'z-report-preview.html';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      revokeLater();
      return 'downloaded';
    } catch {
      try { URL.revokeObjectURL(url); } catch { /* ignore */ }
      return false;
    }
  }

  try { win.focus(); } catch { /* ignore */ }
  revokeLater();
  return true;
}

/** Print Daily Z Report on one A4 page. */
export function printDailyZReport(payload) {
  try {
    return openHtmlDocument(buildPrintHtmlContent(payload), { autoPrint: true });
  } catch (err) {
    console.error('Z Report print failed', err);
    return false;
  }
}

export function exportDailyZReportCsv(payload) {
  downloadCsv(buildCsvContent(payload), `${fileBase(payload)}.csv`);
}

export function exportDailyZReportExcel(payload) {
  downloadBlob(
    `\ufeff${buildExcelHtmlContent(payload)}`,
    `${fileBase(payload)}.xls`,
    'application/vnd.ms-excel;charset=utf-8;',
  );
}

/** Download a real .pdf file (not HTML). */
export function exportDailyZReportPdf(payload) {
  try {
    return downloadPairedZReportPdf(buildPayloadTables(payload), `${fileBase(payload)}.pdf`);
  } catch (err) {
    console.error('Z Report PDF export failed', err);
    return false;
  }
}
